/**
 * Recovery Manager's menu: five groups, by phase of the incident, each holding its views.
 *
 * Andrew, 12 Oct 2026: "The recovery manager menu across the screen is getting big." It was ten
 * tabs in a row and about to be twelve. Grouped, the bar is five entries that follow the order
 * an incident runs in - look, assess, plan, recover, report - and the views of one thing sit
 * together (Canvas, Tracking, Location Tracking and Map are four readings of how the recovery
 * is going). A group remembers the view last used in it.
 *
 * The Technical Checklist and the Impact Assessment are not here: they are their own page on the
 * main menu, before Recovery Manager (Andrew, 13 Oct 2026: "The impact needs to be assessed
 * before they start requesting recoveries"). The second group is Mobilisation alone.
 */
export type ManagerGroup = 'dashboard' | 'assess' | 'plan' | 'recover' | 'report'
export type ManagerView =
    | 'dashboard'
    | 'mobilisation'
    | 'request-ids' | 'recovery-gantt'
    | 'visualization' | 'objects' | 'location-tracking' | 'recovery-map'
    | 'management-updates' | 'post-recovery'

export const MANAGER_GROUPS: ReadonlyArray<{ group: ManagerGroup; views: ReadonlyArray<ManagerView> }> = [
    { group: 'dashboard', views: ['dashboard'] },
    { group: 'assess', views: ['mobilisation'] },
    { group: 'plan', views: ['request-ids', 'recovery-gantt'] },
    { group: 'recover', views: ['visualization', 'objects', 'location-tracking', 'recovery-map'] },
    { group: 'report', views: ['management-updates', 'post-recovery'] },
]

export const groupOf = (view: string): ManagerGroup => MANAGER_GROUPS.find((g) => (g.views as readonly string[]).includes(view))?.group ?? 'dashboard'
export const viewsOf = (group: string): ReadonlyArray<ManagerView> => MANAGER_GROUPS.find((g) => g.group === group)?.views ?? ['dashboard']

/** The view to open when a group is chosen: the one last used there, or its first. */
export function viewForGroup(group: string, lastUsed: Partial<Record<ManagerGroup, ManagerView>>): ManagerView {
    const views = viewsOf(group)
    const remembered = lastUsed[group as ManagerGroup]
    return remembered && views.includes(remembered) ? remembered : views[0]
}
